import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, Shield, CheckCircle, XCircle, Star, Trophy, Loader2,
  AlertTriangle, Lock, Gamepad2, Target, Sparkles, ArrowRight, RefreshCw
} from 'lucide-react';

const DigitalSafetyGames = () => {
  const { childId, gameId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [gamesList, setGamesList] = useState([]);
  const [phase, setPhase] = useState('list'); // list, playing, results
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // For drag-and-drop sorting game
  const [safeItems, setSafeItems] = useState([]);
  const [unsafeItems, setUnsafeItems] = useState([]);
  const [unplacedItems, setUnplacedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null); // For touch-friendly selection

  useEffect(() => {
    fetchGames();
  }, [childId]);

  useEffect(() => {
    if (gameId) {
      fetchGame(gameId);
    }
  }, [gameId]);

  const fetchGames = async () => {
    try {
      const response = await api.get('/digital-safety/games');
      setGamesList(response.data.games || []);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGame = async (id) => {
    setLoading(true);
    try {
      const response = await api.get(`/digital-safety/games/${id}`);
      setGame(response.data);
      
      // Initialize game state based on type
      if (response.data.type === 'drag_drop_sort') {
        setUnplacedItems([...response.data.items]);
        setSafeItems([]);
        setUnsafeItems([]);
      }
      
      setPhase('playing');
      setCurrentItemIndex(0);
      setAnswers({});
    } catch (error) {
      console.error('Failed to fetch game:', error);
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const handleDragToCategory = (item, category) => {
    // Remove from unplaced
    setUnplacedItems(prev => prev.filter(i => i.id !== item.id));
    
    // Add to appropriate category
    if (category === 'SAFE') {
      setSafeItems(prev => [...prev, item]);
    } else {
      setUnsafeItems(prev => [...prev, item]);
    }
    
    // Record answer
    setAnswers(prev => ({
      ...prev,
      [item.id]: category
    }));
  };

  const handleShareOrNot = (item, choice) => {
    setAnswers(prev => ({
      ...prev,
      [item.id]: choice
    }));
    
    // Move to next item or finish
    const items = game.items || game.scenarios || [];
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const handleScamChoice = (scenario, isScam) => {
    setAnswers(prev => ({
      ...prev,
      [scenario.id]: isScam
    }));
    
    if (currentItemIndex < game.scenarios.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const finishDragDropGame = () => {
    calculateResults();
  };

  const calculateResults = async () => {
    let correct = 0;
    let total = 0;
    const detailedResults = [];
    
    if (game.type === 'drag_drop_sort') {
      // Check all sorted items
      const allSorted = [...safeItems, ...unsafeItems];
      total = game.items.length;
      
      game.items.forEach(item => {
        const userCategory = answers[item.id];
        const isCorrect = userCategory === item.correct_category;
        if (isCorrect) correct++;
        
        detailedResults.push({
          id: item.id,
          text: item.text,
          userAnswer: userCategory,
          correctAnswer: item.correct_category,
          correct: isCorrect,
          explanation: item.explanation
        });
      });
    } else if (game.type === 'share_or_not') {
      total = game.items.length;
      
      game.items.forEach(item => {
        const userAnswer = answers[item.id];
        const isCorrect = (userAnswer === 'SHARE') === item.safe_to_share;
        if (isCorrect) correct++;
        
        detailedResults.push({
          id: item.id,
          text: item.text,
          userAnswer: userAnswer,
          correctAnswer: item.safe_to_share ? 'SHARE' : 'PRIVATE',
          correct: isCorrect,
          explanation: item.explanation
        });
      });
    } else if (game.type === 'identify_threats') {
      total = game.scenarios.length;
      
      game.scenarios.forEach(scenario => {
        const userAnswer = answers[scenario.id];
        const isCorrect = userAnswer === scenario.is_scam;
        if (isCorrect) correct++;
        
        detailedResults.push({
          id: scenario.id,
          text: scenario.message,
          userAnswer: userAnswer ? 'SCAM' : 'REAL',
          correctAnswer: scenario.is_scam ? 'SCAM' : 'REAL',
          correct: isCorrect,
          explanation: scenario.explanation,
          red_flags: scenario.red_flags
        });
      });
    }
    
    const score = Math.round((correct / total) * 100);
    const passed = score >= (game.passing_score || 80);
    
    // Submit to backend
    try {
      const response = await api.post(`/digital-safety/games/${game.id}/complete/${childId}`, {
        game_id: game.id,
        score: score,
        correct_count: correct,
        total_count: total,
        time_spent_seconds: 0
      });
      
      setResults({
        score,
        correct,
        total,
        passed,
        xp_earned: response.data.xp_earned,
        details: detailedResults
      });
      
      if (passed) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
      
      setPhase('results');
    } catch (error) {
      console.error('Failed to submit game:', error);
      toast.error('Failed to save game results');
    }
  };

  const resetGame = () => {
    setPhase('list');
    setGame(null);
    setAnswers({});
    setResults(null);
    setSafeItems([]);
    setUnsafeItems([]);
    setUnplacedItems([]);
    setCurrentItemIndex(0);
    navigate(`/digital-safety-games/${childId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-slate-300">Loading safety games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-900 to-slate-900">
      {showConfetti && <Confetti recycle={false} numberOfPieces={400} />}
      
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-4 px-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-white/20"
            onClick={phase === 'playing' ? resetGame : () => navigate(`/learn/${childId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Gamepad2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Safety Games</h1>
              <p className="text-xs text-white/80">Learn while playing!</p>
            </div>
          </div>
          
          <Badge className="bg-amber-500 text-white border-0">
            <Star className="w-3 h-3 mr-1" />
            Fun
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* GAMES LIST */}
          {phase === 'list' && (
            <motion.div
              key="list"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    Digital Safety Mini-Games
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Practice staying safe online with these fun games!
                  </CardDescription>
                </CardHeader>
              </Card>

              <div className="grid gap-4">
                {gamesList.map((gameItem, idx) => (
                  <motion.div
                    key={gameItem.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card 
                      className="bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 transition-all cursor-pointer"
                      onClick={() => navigate(`/digital-safety-games/${childId}/${gameItem.id}`)}
                      data-testid={`game-card-${gameItem.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
                              {gameItem.type === 'drag_drop_sort' && <Target className="w-7 h-7 text-white" />}
                              {gameItem.type === 'share_or_not' && <Lock className="w-7 h-7 text-white" />}
                              {gameItem.type === 'identify_threats' && <AlertTriangle className="w-7 h-7 text-white" />}
                              {gameItem.type === 'drag_drop_build' && <Sparkles className="w-7 h-7 text-white" />}
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg text-white">{gameItem.title}</h3>
                              <p className="text-sm text-slate-400">{gameItem.description}</p>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* PLAYING - Drag Drop Sort Game */}
          {phase === 'playing' && game?.type === 'drag_drop_sort' && (
            <motion.div
              key="drag-sort"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <h2 className="text-xl font-bold text-white mb-2">{game.title}</h2>
                  <p className="text-slate-300">{game.instructions}</p>
                  <div className="mt-3">
                    <Progress 
                      value={((safeItems.length + unsafeItems.length) / game.items.length) * 100} 
                      className="h-2 bg-slate-700"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {safeItems.length + unsafeItems.length} of {game.items.length} sorted
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Drop Zones */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* SAFE Zone */}
                <Card 
                  className="bg-emerald-900/30 border-emerald-700/50 min-h-[200px]"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const itemId = e.dataTransfer.getData('itemId');
                    const item = unplacedItems.find(i => i.id === itemId);
                    if (item) handleDragToCategory(item, 'SAFE');
                  }}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-emerald-400 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      SAFE
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {safeItems.map(item => (
                      <div 
                        key={item.id}
                        className="p-3 bg-emerald-800/30 rounded-lg text-emerald-100 text-sm"
                      >
                        {item.text}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* UNSAFE Zone */}
                <Card 
                  className="bg-red-900/30 border-red-700/50 min-h-[200px]"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const itemId = e.dataTransfer.getData('itemId');
                    const item = unplacedItems.find(i => i.id === itemId);
                    if (item) handleDragToCategory(item, 'UNSAFE');
                  }}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-red-400 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      UNSAFE
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {unsafeItems.map(item => (
                      <div 
                        key={item.id}
                        className="p-3 bg-red-800/30 rounded-lg text-red-100 text-sm"
                      >
                        {item.text}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Unplaced Items - Click/Tap to Select */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-slate-300 text-base">
                    {selectedItem ? 'Now choose SAFE or UNSAFE:' : 'Tap an item to sort it:'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {unplacedItems.map((item, idx) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
                      className={`p-4 rounded-xl text-slate-200 cursor-pointer transition-all ${
                        selectedItem?.id === item.id 
                          ? 'bg-cyan-600/50 border-2 border-cyan-400 ring-2 ring-cyan-400/50' 
                          : 'bg-slate-700/50 hover:bg-slate-700 border-2 border-transparent'
                      }`}
                      data-testid={`selectable-item-${item.id}`}
                    >
                      {item.text}
                    </motion.div>
                  ))}
                </CardContent>
              </Card>

              {/* Sort Buttons - Always visible when an item is selected */}
              {selectedItem && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <p className="text-center text-cyan-300 text-sm">
                    Sorting: <span className="font-semibold">{selectedItem.text.substring(0, 50)}...</span>
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        handleDragToCategory(selectedItem, 'SAFE');
                        setSelectedItem(null);
                      }}
                      className="flex-1 h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                      data-testid="sort-safe-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      SAFE
                    </Button>
                    <Button
                      onClick={() => {
                        handleDragToCategory(selectedItem, 'UNSAFE');
                        setSelectedItem(null);
                      }}
                      className="flex-1 h-14 text-lg bg-red-600 hover:bg-red-700"
                      data-testid="sort-unsafe-btn"
                    >
                      <XCircle className="w-5 h-5 mr-2" />
                      UNSAFE
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Finish Button */}
              {unplacedItems.length === 0 && (
                <Button
                  onClick={finishDragDropGame}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600"
                  data-testid="finish-game-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  See My Results!
                </Button>
              )}
            </motion.div>
          )}

          {/* PLAYING - Share or Not Game */}
          {phase === 'playing' && game?.type === 'share_or_not' && (
            <motion.div
              key="share-not"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <h2 className="text-xl font-bold text-white mb-2">{game.title}</h2>
                  <p className="text-slate-300">{game.instructions}</p>
                  <div className="mt-3">
                    <Progress 
                      value={((currentItemIndex + 1) / game.items.length) * 100} 
                      className="h-2 bg-slate-700"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {currentItemIndex + 1} of {game.items.length}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <Lock className="w-12 h-12 mx-auto text-cyan-400 mb-4" />
                    <h3 className="text-2xl font-bold text-white">
                      {game.items[currentItemIndex]?.text}
                    </h3>
                  </div>

                  <p className="text-slate-400 mb-6">Is it safe to share this online?</p>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => handleShareOrNot(game.items[currentItemIndex], 'SHARE')}
                      className="flex-1 h-16 text-lg bg-emerald-600 hover:bg-emerald-700"
                      data-testid="share-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Safe to SHARE
                    </Button>
                    <Button
                      onClick={() => handleShareOrNot(game.items[currentItemIndex], 'PRIVATE')}
                      className="flex-1 h-16 text-lg bg-red-600 hover:bg-red-700"
                      data-testid="private-btn"
                    >
                      <Lock className="w-5 h-5 mr-2" />
                      Keep PRIVATE
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* PLAYING - Spot the Scam Game */}
          {phase === 'playing' && game?.type === 'identify_threats' && (
            <motion.div
              key="spot-scam"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <h2 className="text-xl font-bold text-white mb-2">{game.title}</h2>
                  <p className="text-slate-300">{game.instructions}</p>
                  <div className="mt-3">
                    <Progress 
                      value={((currentItemIndex + 1) / game.scenarios.length) * 100} 
                      className="h-2 bg-slate-700"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <div className="bg-slate-700/50 rounded-xl p-6 mb-6">
                    <p className="text-white text-lg leading-relaxed">
                      "{game.scenarios[currentItemIndex]?.message}"
                    </p>
                  </div>

                  <p className="text-slate-400 text-center mb-6">Is this a SCAM or REAL?</p>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => handleScamChoice(game.scenarios[currentItemIndex], true)}
                      className="flex-1 h-16 text-lg bg-red-600 hover:bg-red-700"
                      data-testid="scam-btn"
                    >
                      <AlertTriangle className="w-5 h-5 mr-2" />
                      SCAM! 🚨
                    </Button>
                    <Button
                      onClick={() => handleScamChoice(game.scenarios[currentItemIndex], false)}
                      className="flex-1 h-16 text-lg bg-emerald-600 hover:bg-emerald-700"
                      data-testid="real-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      REAL ✓
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* RESULTS */}
          {phase === 'results' && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
                <div className={`p-8 text-center ${
                  results.passed 
                    ? 'bg-gradient-to-r from-emerald-600 to-cyan-600' 
                    : 'bg-gradient-to-r from-amber-600 to-orange-600'
                }`}>
                  {results.passed ? (
                    <>
                      <Trophy className="w-20 h-20 mx-auto mb-4 text-amber-300" />
                      <h2 className="text-3xl font-bold text-white mb-2">Great Job!</h2>
                      <p className="text-white/80">You're a digital safety expert!</p>
                    </>
                  ) : (
                    <>
                      <Shield className="w-20 h-20 mx-auto mb-4 text-white/80" />
                      <h2 className="text-3xl font-bold text-white mb-2">Keep Practicing!</h2>
                      <p className="text-white/80">You'll get better with practice!</p>
                    </>
                  )}
                </div>

                <CardContent className="p-6 space-y-6">
                  {/* Score */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-slate-700/50 rounded-xl">
                      <p className="text-4xl font-bold text-white">{results.score}%</p>
                      <p className="text-sm text-slate-400">Score</p>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-xl">
                      <p className="text-4xl font-bold text-white">{results.correct}/{results.total}</p>
                      <p className="text-sm text-slate-400">Correct</p>
                    </div>
                    <div className="p-4 bg-amber-900/30 rounded-xl">
                      <p className="text-4xl font-bold text-amber-400">+{results.xp_earned}</p>
                      <p className="text-sm text-slate-400">XP</p>
                    </div>
                  </div>

                  {/* Detailed Results */}
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {results.details?.map((result, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-xl border ${
                          result.correct 
                            ? 'bg-emerald-900/20 border-emerald-700/50' 
                            : 'bg-red-900/20 border-red-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {result.correct ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-slate-200 text-sm">{result.text}</p>
                            {!result.correct && (
                              <p className="text-emerald-400 text-xs mt-1">
                                Correct: {result.correctAnswer}
                              </p>
                            )}
                            <p className="text-slate-400 text-xs mt-2">{result.explanation}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={resetGame}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Play Another
                </Button>
                <Button
                  onClick={() => navigate(`/learn/${childId}`)}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
                >
                  Back to Learning
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default DigitalSafetyGames;
